import * as React from "react";
import { WeekDays } from "sh-models/shift/ISmartScheduleShiftEntity";
import KeyboardUtils from "sh-application/utility/KeyboardUtils";

interface UseKeyboardNavigationParams {
    weekdaysCount: number;
    containerRef: React.RefObject<HTMLDivElement>;
    weekdayRefs: React.RefObject<(HTMLDivElement | null)[]>;
    showWeekdays: boolean;
    onToggleDay: (day: WeekDays) => void;
}

interface UseKeyboardNavigationReturn {
    isInteractiveMode: boolean;
    setIsInteractiveMode: (mode: boolean) => void;
    focusedDayIndex: number;
    setFocusedDayIndex: (index: number) => void;
    handleContainerKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
    handleContainerClick: (e: React.MouseEvent<HTMLDivElement>) => void;
    handleWeekdayKeyDown: (e: React.KeyboardEvent<HTMLDivElement>, currentIndex: number, day: WeekDays) => void;
}

/**
 * Custom hook to manage keyboard navigation and focus for the ShiftBlock component
 * Handles Enter/Escape keys for interactive mode and arrow key navigation for weekday selection
 */
export const useKeyboardNavigation = ({
    weekdaysCount,
    containerRef,
    weekdayRefs,
    showWeekdays,
    onToggleDay
}: UseKeyboardNavigationParams): UseKeyboardNavigationReturn => {
    const [isInteractiveMode, setIsInteractiveMode] = React.useState<boolean>(false);
    const [focusedDayIndex, setFocusedDayIndex] = React.useState<number>(0);
    const [shouldAutoFocusWeekday, setShouldAutoFocusWeekday] = React.useState<boolean>(false);

    // Focus management for weekday buttons - only auto-focus when explicitly requested
    React.useEffect(() => {
        if (shouldAutoFocusWeekday && isInteractiveMode && showWeekdays && focusedDayIndex >= 0 && weekdayRefs.current && focusedDayIndex < weekdayRefs.current.length) {
            weekdayRefs.current[focusedDayIndex]?.focus();
            setShouldAutoFocusWeekday(false);
        }
    }, [shouldAutoFocusWeekday, isInteractiveMode, focusedDayIndex, showWeekdays, weekdayRefs]);

    // Handle keyboard navigation on the container
    const handleContainerKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (KeyboardUtils.isEnterPressed(e)) {
            // Enter key: switch to interactive mode if not already
            if (!isInteractiveMode && e.target === e.currentTarget) {
                e.preventDefault();
                setIsInteractiveMode(true);
                // Focus the first interactive element (SlotSelector)
                const firstInteractive = containerRef.current?.querySelector<HTMLElement>(
                    '[role="combobox"], button, input, select, textarea, [tabindex="0"]'
                );
                firstInteractive?.focus();
            }
        } else if (KeyboardUtils.isEscapePressed(e)) {
            // Escape key: if focus is on container or any child, handle it here
            // Check if the event target is within this container
            if (containerRef.current?.contains(e.target as Node)) {
                e.preventDefault();
                e.stopPropagation(); // Prevent the escape from closing the parent dialog

                // If we're in interactive mode or focus is on a child element, return to ShiftBlock container
                if (isInteractiveMode || e.target !== e.currentTarget) {
                    setIsInteractiveMode(false);
                    containerRef.current?.focus();
                }
            }
        }
        // Don't handle arrow keys on the container - let them bubble up for ShiftBlock navigation
    }, [isInteractiveMode, containerRef]);

    // Handle keyboard navigation within weekday buttons
    const handleWeekdayKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLDivElement>, currentIndex: number, day: WeekDays) => {
        if (KeyboardUtils.isRightPressed(e) || KeyboardUtils.isDownPressed(e)) {
            e.preventDefault();
            e.stopPropagation();
            const nextIndex = currentIndex < weekdaysCount - 1 ? currentIndex + 1 : 0;
            setFocusedDayIndex(nextIndex);
            setShouldAutoFocusWeekday(true);
        } else if (KeyboardUtils.isLeftPressed(e) || KeyboardUtils.isUpPressed(e)) {
            e.preventDefault();
            e.stopPropagation();
            const prevIndex = currentIndex > 0 ? currentIndex - 1 : weekdaysCount - 1;
            setFocusedDayIndex(prevIndex);
            setShouldAutoFocusWeekday(true);
        } else if (KeyboardUtils.isEnterPressed(e) || e.key === ' ') {
            e.preventDefault();
            onToggleDay(day);
        }
    }, [weekdaysCount, onToggleDay]);

    // Handle click events (Windows fires click on Enter key press for focusable divs)
    // Per W3C UI Events spec: https://www.w3.org/TR/uievents/#event-type-click
    // The MouseEvent.detail property indicates the number of consecutive clicks.
    // For keyboard-triggered clicks (Enter/Space on focusable elements), detail is 0.
    // For mouse clicks, detail is >= 1 (1 for single click, 2 for double-click, etc.)
    // This behavior is consistent across all modern browsers (Chrome, Firefox, Safari, Edge).
    const handleContainerClick = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        // Check if this click was triggered by keyboard (Enter/Space key)
        // detail === 0 indicates keyboard activation (but on Windows, this might not fire if child consumes keydown)
        // So we also need to handle the case where the container has focus but click bubbles from child
        const containerHasFocus = containerRef.current === document.activeElement;

        // If container has focus and click is coming from within (even if detail === 1), treat as keyboard activation
        // This handles the Windows case where Enter on container generates click on first child
        if (containerHasFocus && !isInteractiveMode) {
            e.preventDefault(); // Prevent any default behavior
            e.stopPropagation(); // Stop the click from doing anything else
            setIsInteractiveMode(true);
            // Focus the first interactive element (SlotSelector)
            const firstInteractive = containerRef.current?.querySelector<HTMLElement>(
                '[role="combobox"], button, input, select, textarea, [tabindex="0"]'
            );
            firstInteractive?.focus();
            return;
        }

        // Standard path: only handle keyboard-triggered clicks on the container itself
        if (e.target === e.currentTarget && e.detail === 0 && !isInteractiveMode) {
            setIsInteractiveMode(true);
            const firstInteractive = containerRef.current?.querySelector<HTMLElement>(
                '[role="combobox"], button, input, select, textarea, [tabindex="0"]'
            );
            firstInteractive?.focus();
            return;
        }
    }, [isInteractiveMode, containerRef]);

    return {
        isInteractiveMode,
        setIsInteractiveMode,
        focusedDayIndex,
        setFocusedDayIndex,
        handleContainerKeyDown,
        handleContainerClick,
        handleWeekdayKeyDown
    };
};
